import type { HTMLAttributes } from 'react';

import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Image } from '@connected/ui/components/image';
import { ScrollArea } from '@connected/ui/components/scroll-area';
import { Separator } from '@connected/ui/components/separator';
import { cn } from '@connected/ui/lib/utils';
import { Link } from '@tanstack/react-router';

import { Module } from './module';

export type ObjectCardProps = HTMLAttributes<HTMLDivElement> & {
    objectId: string;
};

export function ObjectCard({
    className,
    children,
    ...props
}: ObjectCardProps) {
    return (
        <div
            className={cn('flex w-full flex-col gap-4 rounded-lg', className)}
            {...props}
        >
            <div>
                {children}
            </div>
            <Separator />
        </div>
    );
}

ObjectCard.Header = function ObjectCardHeader({
    className,
    children,
    objectId,
    ...props
}: ObjectCardProps) {
    return (
        <Link
            className="cursor-pointer"
            to="/customer/locations/$locationId"
            params={{ locationId: objectId }}
        >
            <div className={cn('w-full', className)} {...props}>
                <span className="text-left text-xl font-bold">{children}</span>
            </div>
        </Link>
    );
};

ObjectCard.Title = function ObjectCardTitle({
    className,
    children,
    ...props
}: HTMLAttributes<HTMLHeadingElement>) {
    return (
        <h2 className={cn('text-xl font-bold', className)} {...props}>
            {children}
        </h2>
    );
};

ObjectCard.SubTitle = function ObjectCardSubTitle({
    className,
    children,
    ...props
}: HTMLAttributes<HTMLHeadingElement>) {
    return (
        <h3
            className={cn(
                'text-base font-normal text-text-primary/60',
                className,
            )}
            {...props}
        >
            {children}
        </h3>
    );
};

ObjectCard.Content = function ObjectCardContent({
    className,
    children,
    ...props
}: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className="w-full">
            <div
                className={cn(
                    'flex w-full flex-col lg:flex-row gap-2 xl:gap-4',
                    className,
                )}
                {...props}
            >
                {children}
            </div>
        </div>
    );
};

ObjectCard.Image = function ObjectCardImage({
    className,
    alt,
    electricalProjectFileId,
    queryOptions,
    objectId,
    enabled,
}: {
    className?: string;
    alt?: string;
    electricalProjectFileId?: string;
    queryOptions?: any;
    objectId: string;
    enabled?: boolean;
}) {
    return (
        <Link
            className={cn('rounded-xl w-full lg:w-1/2 object-cover overflow-clip', className)}
            to="/customer/locations/$locationId"
            params={{ locationId: objectId }}
        >
            <Image
                className="h-full max-w-full object-cover rounded-lg"
                alt={alt || 'Object Image'}
                queryOptions={
                    queryOptions || portaalQueryOptions.files.electricalProject({
                        ElectricalProjectFileId: electricalProjectFileId || '',
                        ImageQuality: 'OriginalWebp',
                    })
                }
                enabled={!!enabled || !!electricalProjectFileId}
            />
        </Link>
    );
};

ObjectCard.Modules = function ObjectCardModules({
    className,
    children,
}: {
    className?: string;
    children: React.ReactNode;
}) {
    return (
        <ScrollArea
            className={cn(
                'flex flex-col gap-2 w-full lg:w-1/2 lg:h-[21.4rem] xl:h-[22.2rem]',
                className,
            )}
        >
            {children}
        </ScrollArea>
    );
};

ObjectCard.Module = Module;
